import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import AppShell from "./AppShell";
import axios from "axios";
import { 
  FileText, Plus,
  DollarSign, Clock, CheckCircle, AlertCircle,
  ChevronRight, Search, Star, ArrowRight
} from "lucide-react";

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [planInfo, setPlanInfo] = useState(null);
  const [ltdStatus, setLtdStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, statsRes, planRes] = await Promise.all([
        axios.get(`${API}/invoices`),
        axios.get(`${API}/invoices/stats`),
        axios.get(`${API}/user/plan`)
      ]);
      setInvoices(invoicesRes.data);
      setStats(statsRes.data);
      setPlanInfo(planRes.data);
      // Fetch LTD status for free users
      if (planRes.data?.plan === "free") {
        try {
          const ltdRes = await axios.get(`${API}/ltd/status`);
          setLtdStatus(ltdRes.data);
        } catch (e) {}
      }
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredInvoices = invoices.filter(inv => {
    const matchesSearch = inv.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.client?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || inv.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const classes = {
      draft: "badge badge-draft",
      sent: "badge badge-sent",
      paid: "badge badge-paid",
      overdue: "badge badge-overdue"
    };
    return <span className={classes[status] || classes.draft}>{status}</span>;
  };

  return (
    <AppShell active="dashboard" planInfo={planInfo}>
      {/* LTD Banner for free users */}
      {ltdStatus?.is_available && planInfo?.plan === "free" && (
        <Link
          to="/lifetime"
          className="flex items-center justify-between p-3 sm:p-4 bg-[#0A0A0A] mb-6 group"
          data-testid="dashboard-ltd-banner"
        >
          <div className="flex items-center gap-2.5">
            <Star className="w-4 h-4 text-[#F59E0B] flex-shrink-0" />
            <span className="text-[13px] sm:text-[14px] text-white">
              <span className="font-bold text-[#F59E0B]">Lifetime deal — $59.</span>
              <span className="hidden sm:inline"> {ltdStatus.remaining} spots left. Pay once, use forever.</span>
              <span className="sm:hidden"> {ltdStatus.remaining} left.</span>
            </span>
          </div>
          <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-[#F59E0B] transition-colors flex-shrink-0" />
        </Link>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6 sm:mb-8">
        <div>
          <h1 className="font-display text-[22px] sm:text-[28px] tracking-[-0.03em] text-[#0A0A0A]">
            Invoices
          </h1>
          <p className="text-[13px] sm:text-[14px] text-[#6B7280] mt-1">Manage and track your invoices</p>
        </div>
        <Link
          to="/invoices/new"
          className="btn-primary flex items-center gap-2"
          data-testid="new-invoice-btn"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">New invoice</span>
          <span className="sm:hidden">New</span>
        </Link>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-white border border-[#E5E7EB] p-3 sm:p-5" data-testid="stat-outstanding">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-[11px] sm:text-[12px] font-medium text-[#6B7280] uppercase tracking-wide">Outstanding</span>
            </div>
            <p className="font-display text-[18px] sm:text-[24px] tracking-[-0.02em] text-[#0A0A0A]">
              {formatCurrency(stats.outstanding)}
            </p>
          </div>
          <div className="bg-white border border-[#E5E7EB] p-3 sm:p-5" data-testid="stat-paid">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-[#10B981]" />
              <span className="text-[11px] sm:text-[12px] font-medium text-[#6B7280] uppercase tracking-wide">Paid</span>
            </div>
            <p className="font-display text-[18px] sm:text-[24px] tracking-[-0.02em] text-[#0A0A0A]">
              {formatCurrency(stats.total_paid)}
            </p>
          </div>
          <div className="bg-white border border-[#E5E7EB] p-3 sm:p-5" data-testid="stat-draft">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-[#6B7280]" />
              <span className="text-[11px] sm:text-[12px] font-medium text-[#6B7280] uppercase tracking-wide">Drafts</span>
            </div>
            <p className="font-display text-[18px] sm:text-[24px] tracking-[-0.02em] text-[#0A0A0A]">
              {stats.draft?.count || 0}
            </p>
          </div>
          <div className="bg-white border border-[#E5E7EB] p-3 sm:p-5" data-testid="stat-overdue">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-[#EF4444]" />
              <span className="text-[11px] sm:text-[12px] font-medium text-[#6B7280] uppercase tracking-wide">Overdue</span>
            </div>
            <p className="font-display text-[18px] sm:text-[24px] tracking-[-0.02em] text-[#0A0A0A]">
              {stats.overdue?.count || 0}
            </p>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center justify-between">
        <div className="relative w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF] pointer-events-none" />
          <input
            type="text"
            placeholder="Search invoices..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full sm:w-64 border border-[#E5E7EB] pl-10 pr-4 py-2.5 text-[14px] bg-white focus:outline-none focus:border-[#1A3DFF]"
            data-testid="search-invoices"
          />
        </div>
        
        {/* Status Filter Tabs */}
        <div className="flex gap-1 bg-[#F3F4F6] p-1 overflow-x-auto w-full sm:w-auto">
          {[
            { value: "all", label: "All" },
            { value: "draft", label: "Draft" },
            { value: "sent", label: "Sent" },
            { value: "paid", label: "Paid" },
            { value: "overdue", label: "Overdue" }
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setStatusFilter(tab.value)}
              className={`px-3 py-1.5 text-[13px] font-medium transition-colors whitespace-nowrap ${
                statusFilter === tab.value
                  ? "bg-white text-[#0A0A0A] shadow-sm"
                  : "text-[#6B7280] hover:text-[#0A0A0A]"
              }`}
              data-testid={`filter-${tab.value}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Invoices List */}
      <div className="bg-white border border-[#E5E7EB] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <p className="text-[14px] text-[#6B7280]">Loading invoices...</p>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="p-12 sm:p-16 text-center">
            <div className="w-12 h-12 bg-[#F3F4F6] flex items-center justify-center mx-auto mb-4">
              <FileText className="w-6 h-6 text-[#9CA3AF]" />
            </div>
            {searchQuery || statusFilter !== "all" ? (
              <>
                <h3 className="font-semibold text-[17px] text-[#0A0A0A] mb-2">No matching invoices</h3>
                <p className="text-[14px] text-[#6B7280] mb-4">Try a different search term or filter.</p>
                <button onClick={() => { setSearchQuery(""); setStatusFilter("all"); }} className="text-[13px] text-[#1A3DFF] hover:underline" data-testid="clear-filters-btn">Clear all filters</button>
              </>
            ) : (
              <>
                <h3 className="font-semibold text-[17px] text-[#0A0A0A] mb-2">No invoices yet</h3>
                <p className="text-[14px] text-[#6B7280] mb-2">Create your first invoice in 30 seconds.</p>
                <p className="text-[13px] text-[#9CA3AF] mb-6">Add a client, your line items, and hit send. It's that fast.</p>
                <Link
                  to="/invoices/new"
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="empty-new-invoice"
                >
                  <Plus className="w-4 h-4" />
                  Create your first invoice
                </Link>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="w-full hidden sm:table">
              <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold text-[12px] text-[#6B7280] uppercase tracking-wide">Invoice</th>
                  <th className="text-left px-5 py-3 font-semibold text-[12px] text-[#6B7280] uppercase tracking-wide">Client</th>
                  <th className="text-left px-5 py-3 font-semibold text-[12px] text-[#6B7280] uppercase tracking-wide">Status</th>
                  <th className="text-right px-5 py-3 font-semibold text-[12px] text-[#6B7280] uppercase tracking-wide">Amount</th>
                  <th className="text-right px-5 py-3 font-semibold text-[12px] text-[#6B7280] uppercase tracking-wide">Due</th>
                  <th className="px-5 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => (
                  <tr 
                    key={invoice.id} 
                    className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB] cursor-pointer transition-colors"
                    onClick={() => navigate(`/invoices/${invoice.id}`)}
                    data-testid={`invoice-row-${invoice.id}`}
                  >
                    <td className="px-5 py-4">
                      <span className="font-mono font-semibold text-[14px] text-[#0A0A0A]">{invoice.invoice_number}</span>
                    </td>
                    <td className="px-5 py-4">
                      <p className="font-medium text-[14px] text-[#0A0A0A]">{invoice.client?.name || "—"}</p>
                      <p className="text-[12px] text-[#6B7280]">{invoice.client?.email || ""}</p>
                    </td>
                    <td className="px-5 py-4">
                      {getStatusBadge(invoice.status)}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <span className="font-mono font-semibold text-[14px] text-[#0A0A0A]">
                        {formatCurrency(invoice.total, invoice.currency)}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right text-[13px] text-[#6B7280]">
                      {invoice.due_date}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Card List */}
            <div className="sm:hidden divide-y divide-[#E5E7EB]">
              {filteredInvoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className="p-4 active:bg-[#F9FAFB] cursor-pointer"
                  onClick={() => navigate(`/invoices/${invoice.id}`)}
                  data-testid={`invoice-card-${invoice.id}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-mono font-semibold text-[14px] text-[#0A0A0A]">{invoice.invoice_number}</span>
                    {getStatusBadge(invoice.status)}
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-[13px] text-[#6B7280]">{invoice.client?.name || "—"}</p>
                    <span className="font-mono font-semibold text-[14px] text-[#0A0A0A]">
                      {formatCurrency(invoice.total, invoice.currency)}
                    </span>
                  </div>
                  <p className="text-[12px] text-[#9CA3AF] mt-1">Due {invoice.due_date}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
};

export default Dashboard;
